import { useQuery } from '@apollo/client';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import ButtonComponent from '../../components/UI/Button/Button.component';
import { apiRoutes } from '../../constants/api-routes.constants';
import { PRIVATE_URIS } from '../../constants/privateURIs.constants';
import { useAuth } from '../../context/AuthContext';
import { GET_POST_SUMMARY } from '../../graphql/resolvers/postQueries/getPostSummary.gql';
import { PostSummaryCard } from './PostSummaryCard';
import './PostSummaryPage.scss';

const PostSummaryPage = () => {
  const { postId } = useParams<{ postId: string }>();
  const { isSignedIn } = useAuth();
  const navigate = useNavigate();
  // Safe by construction: built from the trusted PRIVATE_URIS.POST enum ('/post/:postId'),
  // never user input — so it needs no isSafeRedirectPath check before use as `from`.
  const fullPostPath = PRIVATE_URIS.POST.replace(':postId', postId ?? '');

  const { data, loading } = useQuery(GET_POST_SUMMARY, {
    variables: { postId: postId ?? '' },
    skip: !postId || isSignedIn,
  });

  if (isSignedIn) return <Navigate replace to={fullPostPath} />;

  if (loading) {
    return (
      <div className="LoadingSpinner" role="status" aria-label="Loading" />
    );
  }

  const summary = data?.getPostSummary;

  if (!summary) {
    return (
      <main className="PostSummary PostSummary--empty">
        <p>This post isn&apos;t available.</p>
        <Link to="/">Go home</Link>
      </main>
    );
  }

  return (
    <main className="PostSummary">
      <PostSummaryCard summary={summary} />
      <div className="PostSummary__wall">
        <p>Create a free account to read the full post.</p>
        <div className="PostSummary__actions">
          <ButtonComponent
            variant="primary"
            onClick={() =>
              navigate(apiRoutes.SIGNUP, { state: { from: fullPostPath } })
            }
          >
            Create a free account
          </ButtonComponent>
          <ButtonComponent
            variant="outline"
            onClick={() =>
              navigate(apiRoutes.LOGIN, { state: { from: fullPostPath } })
            }
          >
            Log in
          </ButtonComponent>
        </div>
      </div>
    </main>
  );
};

export default PostSummaryPage;
